import React, { forwardRef, useState, useCallback } from 'react';
import { TextInput } from './TextInput';

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
	label?: string;
	error?: string;
	required?: boolean;
	allowDecimals?: boolean;
	allowNegative?: boolean;
	value?: number | string | undefined;
	onChange?: (value: number | undefined) => void;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	tooltip?: string;
	/**
	 * Helper text under the field. Already reached `TextInput` through the spread below — declaring it
	 * here is what lets a caller pass it.
	 */
	description?: React.ReactNode;
}

/**
 * A modern, Tailwind-styled number input component.
 * Uses an internal string state so the user can freely clear or type partial values.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
	({ allowDecimals = true, allowNegative = true, onChange, value: externalValue, ...props }, ref) => {
		// Convert external value to string for display
		const toDisplayString = (v: number | string | undefined | null): string => {
			if (v == null || v === '') return '';
			return String(v);
		};

		const [localValue, setLocalValue] = useState(() => toDisplayString(externalValue));
		const [isFocused, setIsFocused] = useState(false);

		// When not focused, sync from external value (e.g. form reset, initial load)
		const displayValue = isFocused ? localValue : toDisplayString(externalValue);

		const handleInputChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const val = e.target.value;
				setLocalValue(val);

				if (val === '') {
					onChange?.(undefined);
					return;
				}

				const parsed = allowDecimals ? parseFloat(val) : parseInt(val, 10);
				onChange?.(Number.isNaN(parsed) ? undefined : parsed);
			},
			[onChange, allowDecimals],
		);

		const handleFocus = useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				setIsFocused(true);
				setLocalValue(toDisplayString(externalValue));
				props.onFocus?.(e);
			},
			[externalValue, props.onFocus],
		);

		const handleBlur = useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				setIsFocused(false);
				props.onBlur?.(e);
			},
			[props.onBlur],
		);

		const handleKeyDown = useCallback(
			(e: React.KeyboardEvent<HTMLInputElement>) => {
				const allowedKeys = [
					'Backspace',
					'Delete',
					'Tab',
					'Escape',
					'Enter',
					'Home',
					'End',
					'ArrowLeft',
					'ArrowRight',
				];

				if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
					return;
				}

				const isNumber = /[0-9]/.test(e.key);
				const isDot = e.key === '.';
				const isMinus = e.key === '-';

				if (isNumber) return;
				if (isDot && allowDecimals && !e.currentTarget.value.includes('.')) return;
				if (
					isMinus &&
					allowNegative &&
					e.currentTarget.selectionStart === 0 &&
					!e.currentTarget.value.includes('-')
				)
					return;

				e.preventDefault();
			},
			[allowDecimals, allowNegative],
		);

		return (
			<TextInput
				{...props}
				ref={ref}
				type="text"
				inputMode={allowDecimals ? 'decimal' : 'numeric'}
				value={displayValue}
				onKeyDown={handleKeyDown}
				onChange={handleInputChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
			/>
		);
	},
);
